import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { Subject, map, switchMap } from 'rxjs';

import { SessionDb } from 'lib/cosmos/session/sessionDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { WorkoutDb } from 'lib/cosmos/workout/workoutDb.js';
import { observableHelper } from 'lib/helpers/observable-helper.class.js';
import { ISODate } from 'lib/time/index.js';
import { SelectItemField } from 'lib/ui/form/fields/select-item-field.component.js';
import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { CoreAppComponent } from 'src/app/core/core-app.component.js';

type EditSessionMode = 'full' | 'limited';

@Component({
	selector: 'mod-edit-session',
	templateUrl: './edit-session.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class EditSessionComponent extends CoreAppComponent {
	public readonly editMode = input<EditSessionMode>('full');
	public readonly isInDialog = input<boolean>(false);
	public readonly sessionItem = input.required<SessionDb.ISessionItem>();
	public readonly userItem = input.required<UserDb.IUserItem>();

	public readonly edit = output<SessionDb.ISessionItem>();
	public readonly silentUpdate = output<SessionDb.ISessionItem>();

	private readonly saveSession_ = new Subject<void>();

	public readonly form = {
		id: signal<string | undefined>(undefined),
		name: signal<string | undefined>(undefined),
		state: signal<readonly SessionDb.ISessionStateItem[] | undefined>(undefined),
		notes: signal<string | undefined>(undefined),
		date: signal<ISODate | undefined>(undefined),
		workout: signal<WorkoutDb.IWorkoutItem | undefined>(undefined)
	};

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const form = this.form;

		const name = form.name();
		const date = form.date();
		const sessionItem = this.sessionItem();

		if (!date || !name || !sessionItem) {
			return undefined;
		}

		return this.dependencies.httpService
			.patch<SessionDb.ISessionItem>({
				type: 'session',
				patchModel: {
					id: sessionItem.id,
					name: name,
					notes: form.notes(),
					date: date
				}
			})
			.pipe(
				map((response) => {
					super.notifySaved('Session');
					this.edit.emit(response.data);
				})
			);
	});

	private readonly trainingContainers = signal<readonly UserDb.IUserTrainingContainer[]>([]);

	public readonly workoutSelectionItemsl = computed<SelectItemField<WorkoutDb.IWorkoutItem>[]>(() => {
		const selectionItems: SelectItemField<WorkoutDb.IWorkoutItem>[] = [];

		for (const container of this.trainingContainers()) {
			for (const item of container.items) {
				selectionItems.push({
					data: item.workout,
					title: `${container.title} - ${item.workout.system.name}`,
					value: item.id
				});
			}
		}

		return selectionItems;
	});

	constructor() {
		super();

		this.subscribeToSaveSession();

		effect(() => {
			const user = this.userItem();
			// update form
			this.trainingContainers.set(user?.training?.containers ?? []);
		});
		effect(() => {
			const sessionItem = this.sessionItem();

			// update form
			this.form.id.set(sessionItem.id);
			this.form.name.set(sessionItem.system.name);
			this.form.notes.set(sessionItem.notes);
			this.form.state.set(sessionItem.state);
			this.form.workout.set(sessionItem.workout);
			this.form.date.set(sessionItem.date);
		});
	}

	handleChangeNestedItemCompliance(
		item: SessionDb.ISessionStateNestedItem,
		result: SessionDb.SessionStateResult
	): void {
		item.result = result;
		this.saveSession();
	}

	private subscribeToSaveSession(): void {
		super.subscribeToObservable(
			this.saveSession_.pipe(
				observableHelper.saveDebounceTime({ isMobileView: this.isMobileView() }),
				switchMap(() => {
					const form = this.form;
					const sessionItem = this.sessionItem();

					return this.dependencies.httpService.patch<SessionDb.ISessionItem>({
						type: 'session',
						patchModel: {
							id: sessionItem.id,
							workout: form.workout(),
							state: form.state()
						}
					});
				}),
				map((response) => {
					this.silentUpdate.emit(response.data);
					super.notifySaved('Session');
				})
			)
		);
	}

	private saveSession(): void {
		this.saveSession_.next();
	}
}
